import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Table, Input, Breadcrumb, Form, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import {
  getAllDepartmentList,
  getLocations,
  getRoleList,
  getUserList,
} from "redux/selectors";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import EmployeeProfileModal from "./EmployeeProfileModal";

/* ─── Styled ─────────────────────────────────────────────── */
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  // margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;
const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;
const Score = styled.span`
  font-weight: 600;
  color: ${({ $score }) => {
    if ($score >= 0) return "#22c55e";
    if ($score <= 5) return "#f59e0b";
    return "#ef4444";
  }};
`;

/* ─── Component ─────────────────────────────────────────── */
const EmployeeHistory = ({ onBack }) => {
  const roleList = useSelector(getRoleList);
  const locationList = useSelector(getLocations);
  const departmentList = useSelector(getAllDepartmentList);
  const userList = useSelector(getUserList);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Profile modal
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Filter form — same pattern as HRPerformance
  const [filterForm] = Form.useForm();
  const roleFilter = Form.useWatch("roleFilter", filterForm) || [];
  const deptFilter = Form.useWatch("deptFilter", filterForm) || [];
  const locFilter = Form.useWatch("locFilter", filterForm) || [];
  const catFilter = Form.useWatch("catFilter", filterForm) || [];

  /* ── Fetch employee history list ── */
  const fetchData = async () => {
    try {
      setLoading(true);
      const { code, historyList } = await CALL_API(
        `assets/employees-history/all-employees`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setData(Array.isArray(historyList) ? historyList : []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ── Filters ── */
  const filtered = data.filter((e) => {
    const user = userList?.find((u) => u.userid === e.userid);

    const name = (user?.fullname || e.fullname || "").toLowerCase();
    const empId = (user?.employeeid || e.userid || "").toLowerCase();

    // Search
    if (
      search &&
      !name.includes(search.toLowerCase()) &&
      !empId.includes(search.toLowerCase())
    ) {
      return false;
    }

    // Role Filter
    if (roleFilter.length && !roleFilter.includes(user?.roleid?.[0])) {
      return false;
    }

    // Department Filter
    if (deptFilter.length && !deptFilter.includes(user?.basedepartmentid)) {
      return false;
    }

    // Location Filter
    if (locFilter.length && !locFilter.includes(user?.baselocationid)) {
      return false;
    }

    return true;
  });

  /* ── Table columns ── */
  const columns = [
    {
      title: "Employee Name",
      dataIndex: "userid",
      key: "userid",
      render: (userid, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {userList?.find((item) => item?.userid === userid)?.fullname}
          </div>
        </div>
      ),
    },
    {
      title: "Department",
      dataIndex: "userid",
      key: "userid",
      render: (v) =>
        departmentList?.find(
          (dept) =>
            dept?.department_id ===
            userList?.find((item) => item?.userid === v)?.basedepartmentid
        )?.department_name || "—",
    },
    {
      title: "No. of Services",
      dataIndex: "num_services",
      key: "num_services",
      render: (v) => v ?? 0,
    },
    {
      title: "Services with Part Replacement",
      dataIndex: "replacement_count",
      key: "replacement_count",
      render: (v) => v ?? 0,
    },
    {
      title: "Maintenance Score",
      dataIndex: "num_services",
      key: "num_services",
      render: (s) =>
        s != null ? (
          <Score $score={s}>{10 - s} / 10</Score>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
  ];

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <span
            style={{ cursor: "pointer", color: "#2563eb" }}
            onClick={onBack}
          >
            Asset Management – People View
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Employee History</Breadcrumb.Item>
      </Breadcrumb>

      <Header>
        <Left>
          <span style={{ fontWeight: 500, fontSize: 14, whiteSpace: "nowrap" }}>
            Total Employees: {filtered?.length}
          </span>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search People"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200, borderRadius: 20 }}
          />
          {/* DropdownCheckBox filters — same pattern as HRPerformance */}
          <Form form={filterForm} layout="inline" style={{ marginBottom: 0 }}>
            <Form.Item
              name="roleFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="roleFilter"
                label="Role"
                form={filterForm}
                options={roleList.map((r) => ({
                  label: r.roleName,
                  value: r.roleId,
                }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
            <Form.Item
              name="deptFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="deptFilter"
                label="Department"
                form={filterForm}
                options={departmentList.map((d) => ({
                  label: d.department_name,
                  value: d.department_id,
                }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
            <Form.Item
              name="locFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="locFilter"
                label="Location"
                form={filterForm}
                options={locationList
                  ?.filter((item) => item?.login_from_anywhere !== 1)
                  ?.map((l) => ({
                    label: l.location_name,
                    value: l.location_id,
                  }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Form>
        </Left>
      </Header>

      <Spin spinning={loading}>
        <div style={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={filtered}
            pagination={false}
            size="middle"
            rowKey={(r) => r.userid || r.employeeid}
            onRow={(record) => ({
              onClick: () => {
                setSelectedEmployee(record);
                setProfileOpen(true);
              },
              style: { cursor: "pointer" },
            })}
            style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}
          />
        </div>
      </Spin>

      {/* ── Employee Profile Modal ── */}
      <EmployeeProfileModal
        open={profileOpen}
        onClose={() => {
          setProfileOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
      />
    </div>
  );
};

export default EmployeeHistory;
